import MD5 from 'crypto-js/md5';

const checkIfErrorOccurs = (res) => {
  // console.log(res);
  return {
    code: res.status,
    res,
  };
};

const clearSessionAndRedirect = (reason) => {
  localStorage.removeItem('sessionToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('shofco_username');
  localStorage.removeItem('shofco_email');
  localStorage.removeItem('shofco_role');
  localStorage.removeItem('shofco_role_id');
  localStorage.removeItem('addresses');
  localStorage.removeItem('user');
  localStorage.removeItem('userId');
  localStorage.removeItem('appUserId');
  localStorage.removeItem('memberId');
  window.location = `/login?reason=${reason}`;
};

const TIME_OUT = 100000;

// Single in-flight refresh so concurrent 401s only call /refresh once.
let refreshPromise = null;

async function attemptTokenRefresh() {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return null;

  try {
    const url = `${process.env.REACT_APP_SERVER_URL}/shofco/api/auth/refresh`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!response.ok) return null;
    const data = await response.json();
    if (data && data.authToken) {
      localStorage.setItem('sessionToken', data.authToken);
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
      return data.authToken;
    }
    return null;
  } catch (e) {
    return null;
  }
}

async function customFetch(path, headerOptions) {
  const token = localStorage.getItem('sessionToken');
  if (token) {
    headerOptions.headers = {
      ...headerOptions.headers,
      Authorization: `Bearer ${token}`,
    };
  }

  const res = await timeoutPromise(
    TIME_OUT,
    fetch(path, headerOptions).then(checkIfErrorOccurs).catch(checkIfErrorOccurs),
  );

  if (!res.code) {
    throw { code: 404, message: 'Fail to fetch' };
  }

  if (res.code < 300) {
    return parseResponse(res);
  }

  // 401: try silent refresh once, then retry. Don't retry recursively.
  if (res.code === 401 && !!localStorage.getItem('sessionToken') && !headerOptions._retried) {
    const sessionInvalid = res.res?.headers?.get('X-Session-Invalid');
    if (sessionInvalid === 'true') {
      clearSessionAndRedirect('session_terminated');
      throw { code: 401, message: 'Session terminated.' };
    }

    if (!refreshPromise) {
      refreshPromise = attemptTokenRefresh().finally(() => {
        refreshPromise = null;
      });
    }
    const newToken = await refreshPromise;

    if (newToken) {
      const retryOptions = {
        ...headerOptions,
        _retried: true,
        headers: {
          ...headerOptions.headers,
          Authorization: `Bearer ${newToken}`,
        },
      };
      return customFetch(path, retryOptions);
    }

    clearSessionAndRedirect('expired');
    throw { code: 401, message: 'Session expired. Please login again.' };
  }

  return handleErrorResponse(res);
}

// Helper functions
function parseResponse(res) {
  return res.res.text().then(text => {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  });
}

function handleErrorResponse(res) {
  return res.res.json().catch(() => ({})).then(response => {
    if (res.code === 426) {
      throw {
        code: res.code,
        message: 'We have had some significant upgrades for the app. Please click below to upgrade your app!',
      };
    }

    throw {
      code: res.code,
      message: response.error || response.message || 'Something wrong. Please try again.',
    };
  });
}

export const timeoutPromise = (ms, promise) =>
  new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error('Request time out! Please try again.'));
    }, ms);
    promise.then(
      (res) => {
        clearTimeout(timeoutId);
        resolve(res);
      },
      (err) => {
        clearTimeout(timeoutId);
        reject(err);
      },
    );
  });

export default customFetch;

export function requestWrapper(method) {
  const request = async (url, data = null, params = {}) => {
    let convertUrl = `${process.env.REACT_APP_SERVER_URL}/shofco${url}`;
    // let convertUrl = `http://localhost:3001/api/v1/admin${url}`;

    let convertParams = params;
    let convertData = data || {};

    if (method === 'GET') {
      // is it a GET?
      // GET doesn't have data
      convertParams = convertData;
      if (convertParams !== null) {
        convertUrl = `${convertUrl}${getQueryString(convertParams)
          ? `?${getQueryString(convertParams)}`
          : getQueryString(convertParams)
          }`;
      }
      convertData = null;
    } else if (convertData === Object(convertData)) {
      convertData = JSON.stringify(convertData);
    }

    const timestamp = Date.now();
    const key = MD5(`${timestamp}_LAIDEPTRAI`).toString();
    // default params for fetch = method + (Content-Type)
    const defaults = {
      method,
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        // timestamp,
        // key,
      },
    };
    // check that req url is relative and request was sent to our domain

    const token = localStorage.getItem('sessionToken');
    if (token) {
      defaults.headers.Authorization = `Bearer ${token}`;
    }

    if (method === 'POST' || method === 'PUT') {
      defaults.headers.Accept = 'application/json';
      defaults.headers['Content-Type'] = 'application/json';
    }

    if (convertData) {
      defaults.body = convertData;
    }

    const paramsObj = {
      ...defaults,
      headers: { ...params, ...defaults.headers },
    };
    return customFetch(convertUrl, paramsObj);
  };
  return request;
}

export function getQueryString(params) {
  const esc = encodeURIComponent;
  return Object.keys(params)
    .filter((k) => params[k] || params[k] === 0)
    .map((k) => `${esc(k)}=${esc(params[k])}`)
    .join('&');
}

export const get = requestWrapper('GET');
export const post = requestWrapper('POST');
export const put = requestWrapper('PUT');
export const patch = requestWrapper('PATCH');
export const del = requestWrapper('DELETE');
